import { Icon, type IconName } from '@components/atoms/Icon'
import { Typography } from '@components/atoms/Typography'
import type { ToastVariant } from './toastQueue'
import styles from './Toast.module.scss'

/**
 * `'high'` — the dark solid pill used everywhere in the app (Figma high
 * contrast). `'default'` — the light tinted variant, kept for the Showroom
 * reference only (Figma "default" column).
 */
export type ToastContrast = 'high' | 'default'

// Solid status glyphs. Warning + error share `alert-circle-solid`, differing
// only by colour.
const ICON: Record<ToastVariant, IconName> = {
  success: 'check-circle-solid',
  error: 'alert-circle-solid',
  info: 'info-square-solid',
  warning: 'alert-circle-solid',
}

// Semantic icon colour per variant (independent of the text colour).
const ICON_COLOR: Record<ToastVariant, string> = {
  success: 'var(--colors-foreground-alerts-success-primary)',
  error: 'var(--colors-foreground-alerts-error-primary)',
  info: 'var(--colors-foreground-alerts-info-primary)',
  warning: 'var(--colors-foreground-alerts-warning-secondary)',
}

export interface ToastViewProps {
  variant: ToastVariant
  title?: string
  message: string
  contrast?: ToastContrast
  /** When provided, renders + wires the close button. */
  onDismiss?: () => void
}

/**
 * Presentational toast row shared by the live `ToastContainer` and the
 * Showroom reference. Colours are token-driven, so light/dark theming is
 * automatic.
 */
export function ToastView({
  variant,
  title,
  message,
  contrast = 'high',
  onDismiss,
}: ToastViewProps) {
  // On the high-contrast error pill (solid red) the icon reads white; in every
  // other case it carries the semantic status colour.
  const iconColor =
    contrast === 'high' && variant === 'error'
      ? 'var(--colors-text-white-inverse)'
      : ICON_COLOR[variant]
  const textColor = contrast === 'high' ? 'white-inverse' : 'primary'

  // The header row always holds the icon, the primary line, and the close
  // button (centred together). When a title is present it's the primary line
  // and the message drops below it (aligned to the title's left edge);
  // otherwise the message is the primary line and there's no second row.
  const primaryText = title ?? message
  const secondaryMessage = title ? message : undefined

  return (
    <div
      className={[
        styles.toast,
        contrast === 'default' ? styles.contrastDefault : null,
        styles[`variant-${variant}`],
      ]
        .filter(Boolean)
        .join(' ')}
      role={variant === 'error' ? 'alert' : 'status'}
    >
      <div className={styles.headerRow}>
        <Icon name={ICON[variant]} size={20} color={iconColor} />
        <Typography
          variant="body-md"
          weight={title ? 'semibold' : 'medium'}
          color={textColor}
          as="span"
          className={styles.primaryText}
        >
          {primaryText}
        </Typography>
        <button
          type="button"
          className={styles.dismiss}
          aria-label="Dismiss notification"
          onClick={onDismiss}
        >
          <Icon name="x" size={16} color="currentColor" />
        </button>
      </div>
      {secondaryMessage && (
        <Typography
          variant="body-md"
          color={textColor}
          as="p"
          className={styles.secondaryText}
        >
          {secondaryMessage}
        </Typography>
      )}
    </div>
  )
}
